import { Button, Input, Pagination, Table } from '@/components/commons';
import { ENDPOINT } from '@/services/endpoint';
import { CompanyService } from '@/services/company';
import { convertRequest, notificationMessage, NOTIFY_STATUS } from '@/utils';
import { ERROR_MESSAGE } from '@/utils/constants/messages';
import { APP_ROUTER } from '@/routes/routes';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { TableColumnsType, Tag } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { IGetListCompanyResponse } from '@/types/response/company';
import './style.scss';

interface IProps {
  title?: string;
}

interface IParams {
  name: string;
  page: number;
  limit: number;
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
}

export const CompanyList: React.FC<IProps> = ({ title }) => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [params, setParams] = useState<IParams>({
    name: '',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortDirection: 'DESC',
  });

  const { data, isFetching } = useQuery(
    [ENDPOINT.COMPANY.GET_LIST, params],
    () => CompanyService.getList(convertRequest(params)),
  );

  useEffect(() => {
    const items = data?.data?.data ?? [];
    if (params.page !== 1 && !isFetching && items.length === 0) {
      notificationMessage({
        message: ERROR_MESSAGE.CONFLICT_DATA,
        type: NOTIFY_STATUS.ERROR,
      });
      setParams((prev) => ({ ...prev, page: 1 }));
    }
  }, [data, isFetching, params.page]);

  const onSearch = () => {
    setParams((prev) => ({
      ...prev,
      name: searchInput.trim(),
      page: 1,
    }));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    setParams((prev) => ({ ...prev, page, limit: pageSize }));
  };

  const columns: TableColumnsType<IGetListCompanyResponse> = [
    {
      title: '団体名',
      dataIndex: 'name',
      width: '240px',
    },
    {
      title: '追加日',
      dataIndex: 'createdAt',
      width: '140px',
      render: (value: string) =>
        value ? moment(value).format('YYYY/MM/DD') : '-',
    },
    {
      title: 'コード数',
      dataIndex: 'totalInviteCode',
      width: '120px',
      align: 'center',
    },
    {
      title: '有効ステータス',
      dataIndex: 'status',
      width: '140px',
      align: 'center',
      render: (value: number) => (
        <Tag color={value === 1 ? 'success' : 'default'}>
          {value === 1 ? '有効' : '無効'}
        </Tag>
      ),
    },
  ];

  return (
    <div className='p-post-list'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px]'>
        <header className='flex justify-between w-full'>
          <div className='company-list-title'>
            団体一覧
          </div>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={() => navigate(APP_ROUTER.COMPANY.CREATE)}
          >
            団体の追加
          </Button>
        </header>
        <div className='w-full p-3 bg-white rounded-lg'>
          <Input
            className='w-[400px]'
            placeholder='団体名で検索...'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onPressEnter={onSearch}
          />
        </div>
        <div>
          <Table
            loading={isFetching}
            scroll={{ x: 800 }}
            bordered
            rowKey='id'
            columns={columns}
            dataSource={data?.data?.data ?? []}
            pagination={false}
            onRow={(record) => ({
              onClick: () =>
                navigate(APP_ROUTER.COMPANY.DETAIL.replace(':id', String(record.id))),
            })}
          />
          {
            !!data?.data?.pagination?.total && (<Pagination
              className='pagination-table company-list-pagination'
              total={data?.data.pagination.total ?? 0}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              showTotal={(total, range) =>
                `${range[0]}〜${range[1]}件 / 全${total}件`
              }
              onChange={onChangePagination}
            />)
          }
        </div>
      </div>
    </div>
  );
};
